import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'About MillionDollarMed | Healthcare Marketing Experts | HIPAA-Compliant Growth Partners',
    description: 'Learn about MillionDollarMed, your strategic partner in healthcare growth. We specialize in HIPAA-compliant marketing, automated patient acquisition, and ethical practice growth for preventive medicine, hormone therapy, and wellness providers.',
    keywords: 'healthcare marketing agency, medical marketing experts, HIPAA compliant marketing, healthcare growth partners, preventive medicine marketing, hormone therapy marketing',
    canonical: `${siteUrl}/about`,
    schema: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About MillionDollarMed",
      "description": "Learn about MillionDollarMed, your strategic partner in healthcare growth specializing in HIPAA-compliant marketing and automated patient acquisition",
      "url": `${siteUrl}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "MillionDollarMed",
        "description": "Healthcare digital marketing agency specializing in HIPAA-compliant patient acquisition and practice growth",
        "url": siteUrl,
        "areaServed": "US"
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Your Strategic Partner in Healthcare Growth
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 leading-relaxed">
              We help healthcare providers build sustainable, ethical practices through HIPAA-compliant marketing and intelligent automation
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                At MillionDollarMed, we exist to empower healthcare providers with the marketing systems and automation they need to grow sustainable, patient-centered practices.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                We believe that exceptional medical care deserves exceptional marketing—marketing that is ethical, compliant, and designed to attract the right patients while respecting their privacy and trust.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our focus is on long-term growth, not quick fixes. We build systems that work 24/7 to nurture every lead, automate patient engagement, and help you scale your practice without sacrificing quality or compliance.
              </p>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/e4fcdeda9661d9cd5b7b6460803866ef.jpg"
                alt="MillionDollarMed Team"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Healthcare Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why We Focus Exclusively on Healthcare</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Healthcare marketing isn't like other industries. It requires deep understanding, specialized expertise, and unwavering commitment to compliance.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-hospital-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Clinical Understanding</h3>
              <p className="text-gray-700 leading-relaxed">
                We understand the clinical environment, patient psychology, and the unique challenges healthcare providers face in building trust and attracting the right patients.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">HIPAA Expertise</h3>
              <p className="text-gray-700 leading-relaxed">
                Every system we build is designed with HIPAA compliance in mind. We implement secure data handling, compliant communication channels, and audit-ready processes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Patient-Centered Approach</h3>
              <p className="text-gray-700 leading-relaxed">
                We help you attract patients who are the right fit for your practice, ensuring better outcomes, higher satisfaction, and long-term retention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl order-2 md:order-1">
              <img 
                src="https://readdy.ai/api/search-image?query=healthcare%20digital%20marketing%20dashboard%20with%20analytics%20graphs%20patient%20acquisition%20metrics%20and%20automation%20workflows%20on%20computer%20screens%2C%20modern%20medical%20office%20setting%2C%20professional%20healthcare%20technology%20interface&width=800&height=600&seq=about2&orientation=landscape"
                alt="Healthcare Marketing Expertise"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Deep Healthcare Marketing Expertise</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Specialized in Preventive & Wellness Medicine</h3>
                    <p className="text-gray-700">
                      Hormone therapy, longevity medicine, functional medicine, preventive care, and wellness-focused practices.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Experience with Medical Directors & Clinic Owners</h3>
                    <p className="text-gray-700">
                      We speak your language and understand the business side of running a successful medical practice.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Proven Track Record</h3>
                    <p className="text-gray-700">
                      We've helped hundreds of healthcare providers generate qualified patient leads, automate their marketing, and scale ethically.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Technology-Forward Approach</h3>
                    <p className="text-gray-700">
                      Our proprietary automation platform ensures no lead is ever missed and every patient receives timely, personalized communication.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide every decision we make and every system we build
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance First</h3>
              <p className="text-gray-700">
                HIPAA compliance and patient privacy are non-negotiable in everything we do.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Sustainable Growth</h3>
              <p className="text-gray-700">
                We focus on long-term strategies that build lasting patient relationships.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Ethical Marketing</h3>
              <p className="text-gray-700">
                We use honest, transparent messaging that respects patients and providers.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Partnership Mindset</h3>
              <p className="text-gray-700">
                We're not a vendor—we're your strategic growth partner for the long haul.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HIPAA Commitment Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-purple-600 to-violet-700 rounded-2xl p-12 text-white text-center shadow-2xl">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-lock-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our HIPAA Commitment</h2>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              We understand that patient data protection isn't just a legal requirement—it's a sacred trust. Every marketing system, automation workflow, and communication channel we implement is designed with HIPAA compliance at its core.
            </p>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div>
                <h3 className="text-lg font-bold mb-2">Secure Data Handling</h3>
                <p className="text-purple-100 text-sm">
                  Encrypted storage, secure transmission, and access controls
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-2">Compliant Communication</h3>
                <p className="text-purple-100 text-sm">
                  HIPAA-safe email, SMS, and messaging protocols
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-2">Audit-Ready Systems</h3>
                <p className="text-purple-100 text-sm">
                  Complete activity logs and compliance documentation
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner With Us Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Partner With MillionDollarMed?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're more than a marketing agency—we're your strategic growth partner
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Healthcare-Only Focus</h3>
              <p className="text-gray-700 leading-relaxed">
                We don't work with restaurants, retail, or real estate. We only serve healthcare providers, which means we understand your unique challenges, regulations, and patient acquisition needs better than any generalist agency.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proprietary Automation Platform</h3>
              <p className="text-gray-700 leading-relaxed">
                Our marketing automation system is built specifically for medical practices. It nurtures every lead automatically, books appointments, sends reminders, and ensures no patient inquiry is ever missed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Results</h3>
              <p className="text-gray-700 leading-relaxed">
                We've helped practices increase patient acquisition by 300%, reduce marketing costs by 40%, and save 20+ hours per week through intelligent automation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Long-Term Partnership</h3>
              <p className="text-gray-700 leading-relaxed">
                We're invested in your success for the long haul. We build systems that scale with your practice and provide ongoing optimization, support, and strategic guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Practice?
          </h2>
          <p className="text-xl text-purple-100 mb-10 leading-relaxed">
            Let's discuss how our HIPAA-compliant marketing systems and automation platform can help you attract more qualified patients and scale your practice ethically.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/book-strategy-call"
              className="inline-block px-8 py-4 bg-white text-purple-700 rounded-lg font-bold text-lg hover:bg-purple-50 transition-all shadow-xl whitespace-nowrap"
            >
              Book a Free Strategy Call
            </Link>
            <Link 
              to="/automation-platform"
              className="inline-block px-8 py-4 bg-purple-800/50 text-white rounded-lg font-bold text-lg hover:bg-purple-800/70 transition-all border-2 border-white/30 whitespace-nowrap"
            >
              See Our Platform
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
